import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { Trans } from '@lingui/macro';
import { motion } from 'framer-motion';

import { PlayerMode } from '$/components/WebinarPlayer/types';
import { WebinarPlayerContext, staticWebinarDimensions } from '$/components/WebinarPlayer/context';

import { isMobile } from '$/utils/isMobile';
import EDate from '$/shared/frontend/utils/EDate';
import { langDirClassname } from '$/shared/frontend/utils/i18n';

import { CountdownTimer } from './CountdownTimer';
import { TimedPlayButton } from './TimedPlayButton';
import { useVideoStageAspectScale } from '../useScaledRoomScale';

export const PlayButtonWithReplayExpireTimer: React.FC<{ className?: string }> = ({
  className,
}) => {
  const {
    isPlaying,
    mode,
    setIsPlaying,
    set,
    pauseTimer,
    pauseTimerRemaining,
    pauseMessage,
    attendee,
    setIsReplayExpired,
    ewebinar,
    dimensions,
    mutedByJoiningIssue,
  } = React.useContext(WebinarPlayerContext);

  const expireAfterMins = set.expireReplayLinkAfterMins ?? 0;
  let date: EDate | null = null;

  if (attendee?.startTime && expireAfterMins && mode === PlayerMode.replay) {
    date = new EDate(attendee.startTime, 'UTC').addMinutes(expireAfterMins);
  }

  // DO NOT MAKE THIS CALLBACK ASYNC
  const onClickHandler = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();

      if (
        isPlaying &&
        mode === PlayerMode.live &&
        ewebinar.viewerRoomSettings.enableVideoPause &&
        ewebinar.viewerRoomSettings.videoPauseSecs
      ) {
        // We are pausing the player during a live session with pause enabled
        // Set the timer
        setIsPlaying(false, { secsToPause: ewebinar.viewerRoomSettings.videoPauseSecs });
      } else {
        setIsPlaying(!isPlaying);
      }
    },
    [
      ewebinar.viewerRoomSettings.enableVideoPause,
      ewebinar.viewerRoomSettings.videoPauseSecs,
      isPlaying,
      mode,
      setIsPlaying,
    ]
  );

  const onExpired = React.useCallback((): void => {
    setIsReplayExpired(true);
    setIsPlaying(false);
  }, [setIsPlaying, setIsReplayExpired]);

  const videoStageAspectScale = useVideoStageAspectScale(dimensions);
  const dir = langDirClassname(ewebinar.viewerRoomSettings.language);

  if (mutedByJoiningIssue) {
    return null;
  }
  // Don't show Pause/Play during Scheduled session UNLESS the pause button has a timer (comes from an anchor)
  // or pausing has been enabled
  if (
    [PlayerMode.live, PlayerMode.edit].includes(mode) &&
    !pauseTimer &&
    !ewebinar.viewerRoomSettings?.enableVideoPause
  ) {
    return null;
  }

  return (
    <div
      className={`
        absolute z-player-control  pointer-events-none
        w-full h-full flex flex-col items-center justify-center
        ${className}
      `}
    >
      <motion.div
        className='timed-pause-backdrop'
        css={css`
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          pointer-events: none;
          backdrop-filter: blur(5px);
          background: rgba(0, 0, 0, 0.4);
        `}
        initial={{ opacity: 0 }}
        // Only need backdrop if we have text on the screen or on mobile as controls are over the video
        animate={{ opacity: !isPlaying && (pauseTimer || isMobile) ? 1 : 0 }}
      />

      <TimedPlayButton
        message={pauseMessage}
        duration={pauseTimer}
        remainingTime={pauseTimerRemaining}
        onClick={onClickHandler}
        className='scale-control'
      />

      {date && (
        <div
          className='expires-in-timer flex justify-center items-center'
          css={css`
            position: absolute;
            height: 33%;
            width: 100%;
            max-height: 25rem;
            pointer-events: none;
            bottom: 0;
            &.expires-in-timer::before {
              content: '';
              position: absolute;
              width: 100%;
              height: 100%;
              background: linear-gradient(0deg, #000 0%, #000 40%, #00000000 100%) !important;
              pointer-events: none;
              transition: 0.2s;
              opacity: 0;
            }
          `}
        >
          <div
            id='ExpiresInTimer'
            className={`control scale-control flex font-semibold text-lg md:text-xl ${
              !isMobile ? 'is-mobile flex-col items-center' : 'items-baseline'
            }`}
            css={css`
              position: absolute;
              bottom: ${isMobile
                ? `${
                    dimensions.videoControlsArea.bottomBar.height === 0 /* Over video */
                      ? staticWebinarDimensions.videoControlsArea.bottomBar.height *
                        videoStageAspectScale
                      : 20
                  }px`
                : '20%'};
              text-transform: uppercase;
              > div {
                margin-top: 0.5rem;
              }
            `}
          >
            <span
              className='flex'
              css={
                isMobile
                  ? css`
                      ${tw`items-baseline`}
                      margin: 0 10px -3px 0 !important;
                      font-size: 1.3rem !important;
                    `
                  : css`
                      ${tw`flex-col items-center`}
                      .timer {
                        margin-top: 0.5rem;
                      }
                    `
              }
              dir={dir}
            >
              <Trans>
                Expires in{' '}
                {
                  <CountdownTimer
                    className='timer font-mono'
                    key={null}
                    date={date}
                    onExpired={onExpired}
                    isRtl={dir === 'rtl'}
                  />
                }
              </Trans>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
